import { useState } from "react";
import { motion } from "framer-motion";
import { 
  DollarSign,
  Users,
  Plus,
  Search,
  Bell,
  Settings,
  Filter,
  Download,
  Calendar
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
};

// Before dashboard - compact
const BeforeDash = () => (
  <div className="bg-dashboard-bg rounded p-2.5 border border-problem/30 h-full">
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-1">
        <span className="font-heading font-semibold text-foreground text-[10px]">Dashboard</span>
        <span className="text-[8px] bg-problem-light text-problem-foreground px-1 py-0.5 rounded">Pro</span>
      </div>
      <div className="flex items-center gap-0.5">
        {[Search, Filter, Download, Calendar, Bell, Settings].map((Icon, i) => (
          <button key={i} className="p-0.5 text-muted-foreground"><Icon size={9} /></button>
        ))}
        <button className="px-1 py-0.5 bg-primary text-primary-foreground text-[7px] rounded">New</button>
        <button className="px-1 py-0.5 bg-secondary text-[7px] rounded">Export</button>
      </div>
    </div>
    <div className="grid grid-cols-3 gap-1 mb-2">
      {[{ v: "$24k", l: "Revenue" }, { v: "1,429", l: "Users" }, { v: "42%", l: "Bounce" }].map((s, i) => (
        <div key={i} className="bg-card p-1 rounded border border-dashboard-border">
          <p className="font-semibold text-foreground text-[9px]">{s.v}</p>
          <p className="text-[6px] text-muted-foreground">{s.l}</p>
        </div>
      ))}
    </div>
    <div className="bg-card p-1.5 rounded border border-dashboard-border mb-1.5">
      <div className="flex items-end justify-between h-10 gap-0.5">
        {[65, 45, 78, 52, 88, 42, 95, 67].map((h, i) => (
          <div key={i} className="flex-1 bg-primary/20 rounded-t-sm" style={{ height: `${h}%` }} />
        ))}
      </div>
    </div>
    <div className="flex items-center justify-between text-[7px] pt-1 border-t border-dashboard-border">
      <div className="flex gap-1.5">
        <span className="text-problem">3 urgent</span>
        <span className="text-problem">5 warnings</span>
      </div>
      <span className="text-muted-foreground">12 notifs</span>
    </div>
  </div>
);

// After dashboard - compact
const AfterDash = () => (
  <div className="bg-card rounded p-2.5 border border-improvement/30 h-full">
    <div className="flex items-center justify-between mb-2">
      <div>
        <span className="font-heading font-semibold text-foreground text-[10px]">Dashboard</span>
        <p className="text-[7px] text-muted-foreground">What matters today.</p>
      </div>
      <div className="flex items-center gap-1">
        <button className="p-1 text-muted-foreground"><Search size={10} /></button>
        <button className="p-1 text-muted-foreground relative">
          <Bell size={10} />
          <span className="absolute top-0 right-0 w-1 h-1 bg-improvement rounded-full" />
        </button>
        <button className="px-1.5 py-0.5 bg-primary text-primary-foreground text-[7px] rounded flex items-center gap-0.5">
          <Plus size={7} />Create
        </button>
      </div>
    </div>
    <div className="grid grid-cols-3 gap-1 mb-2">
      <div className="bg-improvement-light rounded p-1.5 border border-improvement/20">
        <p className="font-heading font-bold text-foreground text-[10px]">$24,521</p>
        <p className="text-[6px] text-muted-foreground">Revenue</p>
      </div>
      <div className="bg-secondary rounded p-1.5">
        <p className="font-heading font-bold text-foreground text-[10px]">1,429</p>
        <p className="text-[6px] text-muted-foreground">Users</p>
      </div>
      <div className="bg-secondary rounded p-1.5">
        <p className="font-heading font-bold text-foreground text-[10px]">342</p>
        <p className="text-[6px] text-muted-foreground">Conv.</p>
      </div>
    </div>
    <div className="bg-secondary/50 p-1.5 rounded border border-border mb-1.5">
      <div className="flex items-end justify-between h-12 gap-0.5">
        {[65, 45, 78, 88, 95, 52, 48].map((h, i) => (
          <div key={i} className="flex-1 bg-primary/80 rounded-t" style={{ height: `${h}%` }} />
        ))}
      </div>
    </div>
    <div className="flex items-center justify-between text-[7px] pt-1 border-t border-border">
      <div className="flex items-center gap-1">
        <span className="w-1 h-1 bg-improvement rounded-full" />
        <span className="text-muted-foreground">3 tasks</span>
      </div>
      <span className="text-primary">View →</span>
    </div>
  </div>
);

export const ComparisonSection = () => {
  const [view, setView] = useState("after");

  return (
    <section id="comparison" className="zone-resolution section-tight border-t border-border">
      <div className="container-wide">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mb-5">
          <p className="text-[11px] font-medium text-muted-foreground tracking-wide uppercase mb-1.5">Results</p>
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground mb-2">
            Before vs After
          </h2>
          <p className="text-sm text-muted-foreground max-w-xl">
            Same product. Less effort.
          </p>
        </motion.div>

        {/* Mobile: Tab switcher */}
        <div className="md:hidden">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <div className="flex border border-border rounded-md overflow-hidden mb-3">
              <button 
                onClick={() => setView("before")}
                className={`flex-1 py-2 text-xs font-medium transition-colors ${view === "before" ? "bg-problem-light text-problem-foreground" : "bg-secondary text-muted-foreground"}`}
              >
                Before
              </button>
              <button 
                onClick={() => setView("after")}
                className={`flex-1 py-2 text-xs font-medium transition-colors ${view === "after" ? "bg-improvement-light text-improvement" : "bg-secondary text-muted-foreground"}`}
              >
                After
              </button>
            </div>
            <div className="min-h-[220px]">
              {view === "before" ? <BeforeDash /> : <AfterDash />}
            </div>
          </motion.div>
        </div>

        {/* Desktop: Side by side */}
        <motion.div 
          initial="hidden" 
          whileInView="visible" 
          viewport={{ once: true }} 
          variants={fadeUp}
          className="hidden md:grid md:grid-cols-2 gap-4"
        >
          <div>
            <p className="text-xs font-medium text-problem mb-2 text-center">Before: More options</p>
            <BeforeDash />
          </div>
          <div>
            <p className="text-xs font-medium text-improvement mb-2 text-center">After: Clear decisions</p>
            <AfterDash />
          </div>
        </motion.div>

        <motion.p 
          initial="hidden" 
          whileInView="visible" 
          viewport={{ once: true }} 
          variants={fadeUp}
          className="text-center text-sm text-muted-foreground mt-5 italic"
        >
          Same product. Less effort.
        </motion.p>
      </div>
    </section>
  );
};

export default ComparisonSection;
